import { useState } from "react";
import { Link } from "react-router-dom";

const WITHDRAW_REASONS = [
  "서비스 불만족",
  "이용 빈도 낮음",
  "개인정보 보호 우려",
  "다른 서비스 이용",
  "기타",
];

interface WithdrawResponse {
  success: boolean;
  message: string;
}

function AccountDeletePage() {
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!userId.trim()) {
      setError("사용자 ID를 입력해주세요.");
      return;
    }

    if (!reason) {
      setError("탈퇴 사유를 선택해주세요.");
      return;
    }

    const userIdNum = parseInt(userId, 10);
    if (isNaN(userIdNum)) {
      setError("사용자 ID는 숫자여야 합니다.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        "https://jfuxxeegqerhuhgxzlio.supabase.co/functions/v1/withdraw-user",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmdXh4ZWVncWVyaHVoZ3h6bGlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM3OTI2NjAsImV4cCI6MjA3OTM2ODY2MH0.ur266CqIa5Mht67cbZQhOa2yE09K75cBpPyzML8AKsw",
          },
          body: JSON.stringify({
            user_id: userIdNum,
            reason,
            ...(detail.trim() && { detail: detail.trim() }),
          }),
        }
      );

      const data: WithdrawResponse = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || "탈퇴 처리 중 오류가 발생했습니다.");
      }
    } catch {
      setError("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        {/* Header */}
        <header className="sticky top-0 z-50 w-full bg-gradient-to-r from-[#9b5de5] to-[#f15bb5] shadow-md">
          <div className="container mx-auto px-4 h-16 flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 shadow-lg backdrop-blur-sm flex items-center justify-center">
                <img
                  src="/images/ic_header_logo.svg"
                  alt="LOVATA"
                  className="w-5 h-5 brightness-0 invert"
                />
              </div>
              <span className="text-xl font-extrabold text-white tracking-wide">
                LOVATA
              </span>
            </Link>
          </div>
        </header>

        {/* Success Content */}
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-100 max-w-md w-full text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-green-600"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-4">
              회원 탈퇴 완료
            </h1>
            <p className="text-slate-600 mb-8">
              그동안 LOVATA를 이용해 주셔서 감사합니다.
              <br />
              언제든 다시 찾아주세요.
            </p>
            <Link
              to="/"
              className="inline-block bg-gradient-to-r from-[#9b5de5] to-[#f15bb5] text-white font-bold py-3 px-8 rounded-full hover:opacity-90 transition-opacity"
            >
              홈으로 돌아가기
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-gradient-to-r from-[#9b5de5] to-[#f15bb5] shadow-md">
        <div className="container mx-auto px-4 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 shadow-lg backdrop-blur-sm flex items-center justify-center">
              <img
                src="/images/ic_header_logo.svg"
                alt="LOVATA"
                className="w-5 h-5 brightness-0 invert"
              />
            </div>
            <span className="text-xl font-extrabold text-white tracking-wide">
              LOVATA
            </span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-100 max-w-lg w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-red-500"
              >
                <path d="M3 6h18"></path>
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                <line x1="10" x2="10" y1="11" y2="17"></line>
                <line x1="14" x2="14" y1="11" y2="17"></line>
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              회원 탈퇴
            </h1>
            <p className="text-slate-500 text-sm">
              탈퇴 시 모든 데이터가 삭제되며 복구할 수 없습니다.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User ID */}
            <div>
              <label
                htmlFor="userId"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                사용자 ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="userId"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="사용자 ID를 입력하세요"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#9b5de5] focus:ring-2 focus:ring-[#9b5de5]/20 outline-none transition-all"
              />
            </div>

            {/* Reason */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                탈퇴 사유 <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {WITHDRAW_REASONS.map((r) => (
                  <label
                    key={r}
                    className={`flex items-center p-3 rounded-xl border cursor-pointer transition-all ${
                      reason === r
                        ? "border-[#9b5de5] bg-purple-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      value={r}
                      checked={reason === r}
                      onChange={(e) => setReason(e.target.value)}
                      className="w-4 h-4 text-[#9b5de5] border-slate-300 focus:ring-[#9b5de5]"
                    />
                    <span className="ml-3 text-slate-700">{r}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Detail */}
            <div>
              <label
                htmlFor="detail"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                상세 사유{" "}
                <span className="text-slate-400 font-normal">(선택)</span>
              </label>
              <textarea
                id="detail"
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="추가로 전달하고 싶은 내용이 있다면 작성해주세요."
                rows={4}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#9b5de5] focus:ring-2 focus:ring-[#9b5de5]/20 outline-none transition-all resize-none"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-500 hover:bg-red-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  처리 중...
                </>
              ) : (
                "탈퇴하기"
              )}
            </button>

            {/* Back Link */}
            <div className="text-center">
              <Link
                to="/"
                className="text-slate-500 hover:text-[#9b5de5] text-sm transition-colors"
              >
                취소하고 돌아가기
              </Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

export default AccountDeletePage;
